import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { DocumentCopiesSearchRequest, DocumentCopiesSearchResponse, PatientDto } from 'ts-types/api.types';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import moment from 'moment';
import { BillSearchBy, DocumentCopiesSearchBy } from 'ts-types/api.enums';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { Header, RadioProps } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import styled from 'styled-components';
import { RouteComponentProps } from 'react-router';
import { FormApi, FormState } from 'final-form';
import DatePicker from 'components/final-form/DatePicker';
import { searchDocumentCopies } from 'services/documentCopiesServices';
import { noop } from 'utils/functionUtils';
import Radio from 'components/final-form/Radio';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import { debounce } from 'ts-debounce';
import { isValueEmpty } from 'utils/validatorUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  padding: 1em 1em;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledContainer = styled.div`

   flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }
`;

const StyledHeader = styled(Header)`
  margin-bottom: 1em !important;
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 40px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchGroup = styled.div`
  flex: 1 1 20%;
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;


const StyledSpan = styled.span`
  font-weight: bold;
  padding-left: 1.5rem;
`;

interface Props extends RouteComponentProps<any>, MessageSourceProps, AuthConsumerRenderProps {
  currentPatient?: PatientDto,
  onFilterBills: (documents: Array<DocumentCopiesSearchResponse>) => void,
  onBillsLoadingChange: (documentsLoading: boolean) => void,
}

interface ExtendedDocumentCopiesSearchRequest extends DocumentCopiesSearchRequest {
  billFromDate: string,
  billToDate: string,
  reminderFromDate: string,
  reminderToDate: string,
}

let initialValues: Partial<ExtendedDocumentCopiesSearchRequest> = {
  searchBy: BillSearchBy.BILL_NUMBER,
  billFromDate: moment().format("YYYY-MM-DD"),
  billToDate: moment().format("YYYY-MM-DD"),
  reminderFromDate: moment().format("YYYY-MM-DD"),
  reminderToDate: moment().format("YYYY-MM-DD")
}

class DocumentCopiesSearchSegment extends Component<Props> {

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();
  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  constructor(props: Props) {
    super(props);

    const {currentPatient, currentUser, getMessage} = this.props;
    const {radioMap} = this;

    const state: any = this.props.location.state;
    const selectedBillId = (state && state.selectedBill) ? state.selectedBill.id : undefined;

    const searchByValues = Object.values(DocumentCopiesSearchBy);

    const radioDefinitions = generateRadioButtonOptionsFromEnum(searchByValues, "searchBy", getMessage);

    radioDefinitions.forEach((radioDefinition, index) => {
      radioMap.set(searchByValues[index], radioDefinition);
    });

    if (selectedBillId) {
      initialValues.searchBy = BillSearchBy.BILL_NUMBER;
      initialValues.fromBillNumber = selectedBillId;
    } else if (currentPatient) {
      initialValues.searchBy = BillSearchBy.CURRENT_PATIENT;
      initialValues.patientId = currentPatient.id;
      initialValues.fromBillNumber = undefined;
    }

    initialValues.mandateId = currentUser ? currentUser.userConfig.mandateId : undefined;
  };

  shouldSearchBills = (values: Partial<ExtendedDocumentCopiesSearchRequest>): boolean => {

    const {searchBy, fromBillNumber, toBillNumber, fromAmount, toAmount} = values;

    switch (searchBy) {
      case DocumentCopiesSearchBy.BILL_NUMBER: {
        return !isValueEmpty(fromBillNumber) || !isValueEmpty(toBillNumber);
      }
      case DocumentCopiesSearchBy.AMOUNT: {
        return !isValueEmpty(fromAmount) || !isValueEmpty(toAmount);
      }
      default: {
        return true;
      }
    }
  };

  handleChange = ({values}: FormState, form: FormApi) => {

    const {onBillsLoadingChange} = this.props;

    const billFromDate = values.billFromDate && new Date(values.billFromDate);
    const billToDate = values.billToDate && new Date(values.billToDate);
    const reminderFromDate = values.reminderFromDate && new Date(values.reminderFromDate);
    const reminderToDate = values.reminderToDate && new Date(values.reminderToDate);

    if (this.shouldSearchBills(values)) {
      onBillsLoadingChange(true);
      this.searchDocumentCopies(values);
    }

    if (billFromDate && billToDate && billFromDate.getTime() > billToDate.getTime()) {
      form.change("billToDate", moment(billFromDate).format("YYYY-MM-DD"));
    }
    if (reminderFromDate && reminderToDate && reminderFromDate.getTime() > reminderToDate.getTime()) {
      form.change("reminderToDate", moment(reminderFromDate).format("YYYY-MM-DD"));
    }
  };

  searchDocumentCopies = debounce((values: Partial<ExtendedDocumentCopiesSearchRequest>) => {

    const {onBillsLoadingChange, onFilterBills} = this.props;

    onBillsLoadingChange(true);

    let request: Partial<ExtendedDocumentCopiesSearchRequest> = {
      ...values
    }
    if (values.searchBy === DocumentCopiesSearchBy.BILL_DATE) {
      request.fromDate = values.billFromDate;
      request.toDate = values.billToDate;
    }
    if (values.searchBy === DocumentCopiesSearchBy.REMINDER_DATE) {
      request.fromDate = values.reminderFromDate;
      request.toDate = values.reminderToDate;
    }

    searchDocumentCopies(request, this.cancelTokenSource)
        .then(response => {
          onFilterBills(response);
        })
        .finally(() => {
          onBillsLoadingChange(false);
        })
  },400);

  render(): React.ReactElement {

    return (
        <FinalForm
            onSubmit={() => noop}
            initialValues={initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    const {radioMap, props} = this;
    const {currentPatient, getMessage} = props;

    const billNumberRadioDefinition = radioMap.get(DocumentCopiesSearchBy.BILL_NUMBER);
    const currentPatientRadioDefinition = radioMap.get(DocumentCopiesSearchBy.CURRENT_PATIENT);
    const amountRadioDefinition = radioMap.get(DocumentCopiesSearchBy.AMOUNT);
    const billDateRadioDefinition = radioMap.get(DocumentCopiesSearchBy.BILL_DATE);
    const reminderDateRadioDefinition = radioMap.get(DocumentCopiesSearchBy.REMINDER_DATE);

    const disabledBillBefore = values.billFromDate && new Date(values.billFromDate);
    const disabledReminderBefore = values.reminderFromDate && new Date(values.reminderFromDate);

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <StyledHeader>{getMessage("searchOptions")}</StyledHeader>
          </StyledContainer>
          <StyledContainer>
            <SearchOptionsContainer>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={billNumberRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="fromBillNumber"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("fromBillNumber")}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="toBillNumber"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("toBillNumber")}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      disabled={!currentPatient}
                      radioDefinition={currentPatientRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  {currentPatient &&
                  <StyledSpan>
                    {`${currentPatient.name} ${currentPatient.firstName}`}
                  </StyledSpan>
                  }
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={amountRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromAmount"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("fromAmount")}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toAmount"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("toAmount")}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={billDateRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="billFromDate"
                      component={DatePicker}
                      emptyDateInput
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="billToDate"
                      component={DatePicker}
                      disabledBefore={disabledBillBefore}
                      emptyDateInput
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={reminderDateRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="reminderFromDate"
                      component={DatePicker}
                      emptyDateInput
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="reminderToDate"
                      component={DatePicker}
                      disabledBefore={disabledReminderBefore}
                      emptyDateInput
                      fluid
                  />
                </SearchField>
              </SearchGroup>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </StyledForm>
    )
  }

}

export default withRouterWorkaround(withAuthContext(withMessages(DocumentCopiesSearchSegment)));
